import { FolderOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { Button, Empty, Space, Table, Tooltip, theme } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from 'components/headings';
import DocumentosKpiStrip from 'components/admin/Folders/DocumentosKpiStrip';
import FoldersToolbar from 'components/admin/Folders/FoldersToolbar';
import whitDoc from 'components/HOCs/documentos/whitDoc';
import { useDocumentosList } from 'hooks/useApi/useDocumentosApi';
import { useActionsPresupuestos } from 'providers/Administradores/ActionsPresupuestoProvider';
import { useActionsODT } from 'providers/ActionsOdtProvider';
import { useActionsComprobantes } from 'providers/ActionsComprobantesProvider';
import { useActionsMantencionesDep } from 'providers/ActionsMantencionDepProvider';
import { useSolicitudesMantencion } from 'providers/SolicitudesMantencionProvider';
import paths from 'assets/routes/paths';

const { useToken } = theme;

// `whitDoc(Table)` produce un componente estable — definirlo FUERA del
// componente evita remounts en cada render (bug latente del Folders
// anterior, que llamaba whitDoc dentro del render).
const TableComponent = whitDoc(Table);

const SEARCH_SUPPORTED = new Set(['presupuestos', 'comprobantes']);

function Folders() {
  const { token } = useToken();
  const navigate = useNavigate();

  const [type, setType] = useState('presupuestos');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    idClient: null,
    dateFrom: null,
    dateTo: null,
  });
  const [refreshKey, setRefreshKey] = useState(0);

  const { rows, total, loading, refetch } = useDocumentosList({
    type,
    page,
    pageSize,
    search: filters.search,
    idClient: filters.idClient,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  });

  const { dowload: dowloadPresupuesto } = useActionsPresupuestos();
  const { dowload: dowloadODT } = useActionsODT();
  const { dowload: dowloadComprobante } = useActionsComprobantes();
  const { dowload: dowloadMantencionDep } = useActionsMantencionesDep();
  const { openDowloadMantencion } = useSolicitudesMantencion();

  const bumpAndRefetch = useCallback(() => {
    setRefreshKey((k) => k + 1);
    refetch();
  }, [refetch]);

  const handleTypeSelect = useCallback((next) => {
    setType(next);
    setPage(1);
    // El rango de fechas se mantiene al cambiar de tipo; búsqueda y cliente
    // se resetean (son específicos del listado activo).
    setFilters((f) => ({ search: '', idClient: null, dateFrom: f.dateFrom, dateTo: f.dateTo }));
  }, []);

  const handleFiltersChange = useCallback((next) => {
    setFilters(next);
    setPage(1);
  }, []);

  const customActions = useMemo(
    () => ({
      presupuestos: (presupuesto) => (
        <Space>
          <Tooltip title="Descargar Presupuesto">
            <Button
              type="primary"
              size="small"
              onClick={() => {
                if (presupuesto.jsonDocument) {
                  navigate(paths.admin.presupuestos.documentos(presupuesto.id));
                } else {
                  dowloadPresupuesto(presupuesto.id);
                }
              }}
              icon={<DownloadOutlined />}
            />
          </Tooltip>
          {presupuesto.stage === 'finalizada' && (
            <Tooltip title="Descargar ODT">
              <Button
                size="small"
                onClick={() => dowloadODT(presupuesto.id)}
                icon={<DownloadOutlined />}
              >
                ODT
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
      comprobantes: (comprobante) => (
        <Tooltip title="Descargar Comprobante">
          <Button
            type="primary"
            size="small"
            onClick={() => dowloadComprobante(comprobante.id)}
            icon={<DownloadOutlined />}
          />
        </Tooltip>
      ),
      mantencionesDep: (mantencionDep) => (
        <Tooltip title="Descargar Mantención">
          <Button
            type="primary"
            size="small"
            onClick={() => dowloadMantencionDep(mantencionDep.id)}
            icon={<DownloadOutlined />}
          />
        </Tooltip>
      ),
      mantenciones: (mantencion) => (
        <Tooltip title="Descargar Mantención">
          <Button
            type="primary"
            size="small"
            onClick={() => openDowloadMantencion({ id_mantencion: mantencion.id })}
            icon={<DownloadOutlined />}
          />
        </Tooltip>
      ),
    }),
    [
      dowloadPresupuesto,
      dowloadODT,
      dowloadComprobante,
      dowloadMantencionDep,
      openDowloadMantencion,
      navigate,
    ]
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: token.marginLG,
        paddingTop: token.paddingMD,
        paddingBottom: token.paddingXL,
      }}
    >
      <PageHeader
        icon={FolderOutlined}
        title="Gestión de Documentos"
        subtitle="Administra y descarga todos los documentos del sistema"
        actions={
          <Button icon={<ReloadOutlined />} onClick={bumpAndRefetch}>
            Actualizar
          </Button>
        }
      />

      <DocumentosKpiStrip activeType={type} refreshKey={refreshKey} onSelect={handleTypeSelect} />

      <FoldersToolbar
        filters={filters}
        onChange={handleFiltersChange}
        supportsSearch={SEARCH_SUPPORTED.has(type)}
      />

      {rows.length === 0 && !loading ? (
        <div
          style={{
            padding: token.paddingXL,
            border: `1px solid ${token.colorBorder}`,
            borderRadius: token.borderRadiusLG,
            background: token.colorBgContainer,
          }}
        >
          <Empty description="No hay documentos con los filtros aplicados" />
        </div>
      ) : (
        <TableComponent
          type={type}
          rows={rows}
          loading={loading}
          customActions={customActions}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              if (nextPageSize && nextPageSize !== pageSize) setPageSize(nextPageSize);
            },
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            showTotal: (t, range) => `${range[0]}-${range[1]} de ${t} documentos`,
          }}
        />
      )}
    </div>
  );
}

export default Folders;
